import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import "./ChatsTab.css";
import { toast } from "react-toastify";


const ChatsTab = ({ employee }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState({});
  const [typing, setTyping] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteChatModal, setShowDeleteChatModal] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [lastSeen, setLastSeen] = useState({});

  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatMenuRef = useRef(null);
  const ws = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Emoji list
  const emojis = ['😀', '😂', '😍', '🥰', '😊', '🤔', '😎', '🥳', '😢', '😡', '❤️', '👍', '👋', '🎉', '🔥', '⭐', '📁', '✅', '❌', '💡', '🎯', '✨', '💯', '👏'];

  useEffect(() => {
    if (employee) {
      fetchConversations();
      connectWebSocket();
      // Set current user as online
      updateOnlineStatus(employee._id, true);
    }

    return () => {
      if (ws.current) {
        ws.current.close();
      }
      // Set current user as offline when leaving
      if (employee) {
        updateOnlineStatus(employee._id, false);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [employee]);

  // Auto-scroll to bottom only when near bottom
  useEffect(() => {
    if (isNearBottom) {
      scrollToBottom();
    }
  }, [messages, isNearBottom]);

  // Close emoji picker and chat menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
        setShowChatMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const connectWebSocket = () => {
    if (!employee) return;

    // WebSocket connection
    const wsUrl = API_BASE_URL.replace('http', 'ws') + `/ws/messages/${employee._id}`;
    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log('WebSocket connected');
      // Send online status when connected
      sendWebSocketMessage({
        type: 'USER_ONLINE',
        userId: employee._id
      });
    };

    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
      // Set offline status when disconnected
      if (employee) {
        updateOnlineStatus(employee._id, false);
      }
      // Attempt to reconnect after 5 seconds
      setTimeout(() => {
        if (employee) {
          connectWebSocket();
        }
      }, 5000);
    };

    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'NEW_MESSAGE':
        handleNewMessage(data.message);
        break;
      case 'MESSAGE_DELETED':
        handleMessageDeleted(data.messageId);
        break;
      case 'TYPING_START':
        handleTypingStart(data.senderId);
        break;
      case 'TYPING_STOP':
        handleTypingStop(data.senderId);
        break;
      case 'USER_ONLINE':
        handleUserOnline(data.userId, data.timestamp);
        break;
      case 'USER_OFFLINE':
        handleUserOffline(data.userId, data.timestamp);
        break;
      case 'MESSAGE_READ':
        handleMessageRead(data.messageId, data.readerId);
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  };

  const handleNewMessage = (message) => {
    // If the message belongs to the current conversation
    if (selectedConversation && 
        (message.sender._id === selectedConversation._id || message.sender._id === employee._id)) {
      setMessages(prev => {
        // Avoid duplicates
        if (!prev.find(msg => msg._id === message._id)) {
          return [...prev, message];
        }
        return prev;
      });

      // If it's a message from the other person in current chat, mark as read
      if (message.sender._id === selectedConversation._id) {
        markMessageAsRead(message._id);
      }
    }
    
    // Update conversations list
    fetchConversations();
  };

  const handleMessageDeleted = (messageId) => {
    setMessages(prev => prev.filter(msg => msg._id !== messageId));
    fetchConversations();
  };

  const handleTypingStart = (senderId) => {
    if (selectedConversation && senderId === selectedConversation._id) {
      setTyping(true);
    }
  };

  const handleTypingStop = (senderId) => {
    if (selectedConversation && senderId === selectedConversation._id) {
      setTyping(false);
    }
  };

  const handleUserOnline = (userId, timestamp) => {
    setOnlineStatus(prev => ({ ...prev, [userId]: true }));
    if (timestamp) {
      setLastSeen(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleUserOffline = (userId, timestamp) => {
    setOnlineStatus(prev => ({ ...prev, [userId]: false }));
    if (timestamp) {
      setLastSeen(prev => ({ ...prev, [userId]: timestamp }));
    }
  };

  const handleMessageRead = (messageId, readerId) => {
    setMessages(prev => prev.map(msg => 
      msg._id === messageId ? { ...msg, readBy: [...(msg.readBy || []), readerId] } : msg
    ));
  };

  const updateOnlineStatus = (userId, isOnline) => {
    sendWebSocketMessage({
      type: isOnline ? 'USER_ONLINE' : 'USER_OFFLINE',
      userId: userId,
      timestamp: new Date().toISOString()
    });
  };

  const markMessageAsRead = async (messageId) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/messages/mark-read`, {
        messageId: messageId,
        readerId: employee._id
      });

      if (response.data.success) {
        // Send read receipt via WebSocket
        sendWebSocketMessage({
          type: 'MESSAGE_READ',
          messageId: messageId,
          readerId: employee._id
        });
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markConversationAsRead = async () => {
    if (!selectedConversation) return;

    try {
      const unreadMessages = messages.filter(msg => 
        msg.sender._id === selectedConversation._id && 
        !msg.readBy?.includes(employee._id)
      );

      for (const message of unreadMessages) {
        await markMessageAsRead(message._id);
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  const sendWebSocketMessage = (message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: "smooth",
      block: "nearest"
    });
  };

  const handleScroll = () => {
    if (chatMessagesRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setIsNearBottom(distanceFromBottom <= 100);

      // Auto-mark as read when scrolling near bottom
      if (distanceFromBottom <= 300) {
        markConversationAsRead();
      }
    }
  };

  const scrollToBottomButton = () => {
    setIsNearBottom(true);
    scrollToBottom();
    markConversationAsRead();
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversations/${employee._id}`
      );
      
      if (response.data.success) {
        setConversations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (participant) => {
    try {
      setMessageLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversation/${employee._id}/${participant._id}?currentUserId=${employee._id}`
      );
      
      if (response.data.success) {
        setMessages(response.data.data);
        setSelectedConversation(participant);
        setIsNearBottom(true);
        
        // Mark all messages as read when opening conversation
        markConversationAsRead();
        
        fetchConversations();
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    } finally {
      setMessageLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    try {
      setSending(true);
      
      const messageData = {
        sender: employee._id,
        receiver: selectedConversation._id,
        message: newMessage,
        messageType: 'text'
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/messages/send`,
        messageData
      );

      if (response.data.success) {
        // Send via WebSocket for real-time delivery
        sendWebSocketMessage({
          type: 'NEW_MESSAGE',
          message: response.data.data
        });

        setMessages(prev => [...prev, response.data.data]);
        setNewMessage("");
        setIsNearBottom(true);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    // Send typing start
    sendWebSocketMessage({
      type: 'TYPING_START',
      senderId: employee._id,
      receiverId: selectedConversation._id
    });

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to send typing stop after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      sendWebSocketMessage({
        type: 'TYPING_STOP',
        senderId: employee._id,
        receiverId: selectedConversation._id
      });
    }, 2000);
  };

  // Message deletion
  const handleDeleteMessage = (message) => {
    setMessageToDelete(message);
    setShowDeleteModal(true);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/messages/${messageToDelete._id}`
      );

      if (response.data.success) {
        // Send via WebSocket for real-time update
        sendWebSocketMessage({
          type: 'MESSAGE_DELETED',
          messageId: messageToDelete._id,
          conversationId: [employee._id, selectedConversation._id].sort().join('_')
        });

        setMessages(prev => prev.filter(msg => msg._id !== messageToDelete._id));
        setShowDeleteModal(false);
        setMessageToDelete(null);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message. Please try again.');
    }
  };

  const cancelDeleteMessage = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };

  // Chat history deletion
  const handleDeleteChatHistory = () => {
    setShowDeleteChatModal(true);
    setShowChatMenu(false);
  };

  const confirmDeleteChatHistory = async () => {
    if (!selectedConversation) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/messages/conversation/${employee._id}/${selectedConversation._id}`
      );

      if (response.data.success) {
        setMessages([]);
        setShowDeleteChatModal(false);
        fetchConversations();
        toast.success('Chat history deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting chat history:', error);
      toast.error('Failed to delete chat history. Please try again.');
    }
  };

  const cancelDeleteChatHistory = () => {
    setShowDeleteChatModal(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sender', employee._id);
      formData.append('receiver', selectedConversation._id);

      const response = await axios.post(
        `${API_BASE_URL}/api/messages/send-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Send via WebSocket for real-time delivery
        sendWebSocketMessage({
          type: 'NEW_MESSAGE',
          message: response.data.data
        });

        setMessages(prev => [...prev, response.data.data]);
        setIsNearBottom(true);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file. Please try again.');
    } finally {
      e.target.value = '';
    }
  };

  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const toggleChatMenu = () => {
    setShowChatMenu(!showChatMenu);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const now = new Date();
    const lastSeenDate = new Date(timestamp);
    const diffInMinutes = Math.floor((now - lastSeenDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const messageDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (messageDate.toDateString() === today.toDateString()) {
      return formatTime(timestamp);
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const truncateMessage = (message, length = 35) => {
    if (!message) return 'No messages yet';
    return message.length > length ? message.substring(0, length) + '...' : message;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(" ").map((n) => n[0]).join("").toUpperCase();
  };

  const getOnlineStatus = (participantId) => {
    return onlineStatus[participantId] ? 'Online' : 'Offline';
  };

  const getStatusColor = (participantId) => {
    return onlineStatus[participantId] ? '#4CAF50' : '#9E9E9E';
  };

  const getLastSeenText = (participantId) => {
    if (onlineStatus[participantId]) {
      return 'Online';
    } else {
      const timestamp = lastSeen[participantId];
      return `Last seen ${formatLastSeen(timestamp)}`;
    }
  };

  const isMessageRead = (message) => {
    if (message.sender._id !== employee._id) return false;
    return message.readBy && message.readBy.includes(selectedConversation._id);
  };

  const renderEmojiPicker = () => {
    if (!showEmojiPicker) return null;

    return (
      <div className="emoji-picker" ref={emojiPickerRef}>
        <div className="emoji-picker-header">
          <span>Select Emoji</span>
          <button 
            className="emoji-picker-close"
            onClick={() => setShowEmojiPicker(false)}
          >
            ×
          </button>
        </div>
        <div className="emoji-grid">
          {emojis.map((emoji, index) => (
            <button
              key={index}
              className="emoji-btn"
              onClick={() => addEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="delete-modal-overlay">
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Delete Message</h3>
            <p>Are you sure you want to delete this message? This action cannot be undone.</p>
            <div className="delete-modal-actions">
              <button 
                className="cancel-delete-btn"
                onClick={cancelDeleteMessage}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={confirmDeleteMessage}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteChatModal = () => {
    if (!showDeleteChatModal) return null;

    return (
      <div className="delete-modal-overlay">
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Delete Chat History</h3>
            <p>Are you sure you want to delete entire chat history with {selectedConversation?.name}? This action cannot be undone.</p>
            <div className="delete-modal-actions">
              <button 
                className="cancel-delete-btn"
                onClick={cancelDeleteChatHistory}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={confirmDeleteChatHistory}
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderChatMenu = () => {
    if (!showChatMenu) return null;

    return (
      <div className="chat-menu" ref={chatMenuRef}>
        <button 
          className="chat-menu-item"
          onClick={handleDeleteChatHistory}
        >
          <span className="menu-icon">🗑️</span>
          Delete Chat History
        </button>
        <button className="chat-menu-item">
          <span className="menu-icon">🔕</span>
          Mute Notifications
        </button>
        <button className="chat-menu-item">
          <span className="menu-icon">🚫</span>
          Block User
        </button>
        <button className="chat-menu-item">
          <span className="menu-icon">📋</span>
          Export Chat
        </button>
      </div>
    );
  };

  return (
    <div className="chats-tab-container">
      <div className="chats-tab-header">
        <h3 className="chats-tab-title">HR Copilot Chat</h3>
        <p className="chats-tab-subtitle">Real-time messaging with your team</p>
      </div>

      <div className="chats-tab-content">
        {/* Conversations List */}
        <div className="conversations-list">
          <div className="conversations-search">
            <div className="search-container">
              <span className="search-icon">🔍</span>
              <input 
                type="text" 
                placeholder="Search conversations..."
                className="search-input"
              />
            </div>
          </div>

          <div className="conversations-header">
            <h4>Conversations</h4>
            <span className="conversations-count">
              {conversations.length} {conversations.length === 1 ? 'chat' : 'chats'}
            </span>
          </div>

          {loading ? (
            <div className="conversations-loading">
              <div className="whatsapp-spinner"></div>
              <p>Loading conversations...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="conversations-empty">
              <div className="conversations-empty-icon">💬</div>
              <p>No conversations yet</p>
              <p className="conversations-empty-subtext">
                Start a conversation from the My Team tab
              </p>
            </div>
          ) : (
            <div className="conversations-items">
              {conversations.map((conversation) => (
                <div
                  key={conversation.conversationId}
                  className={`conversation-item ${
                    selectedConversation?._id === conversation.participant._id ? 'active' : ''
                  }`}
                  onClick={() => fetchConversation(conversation.participant)}
                >
                  <div className="conversation-avatar">
                    {getInitials(conversation.participant.name)}
                    <div 
                      className="online-indicator"
                      style={{ backgroundColor: getStatusColor(conversation.participant._id) }}
                    ></div>
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <h5 className="conversation-name">
                        {conversation.participant.name}
                      </h5>
                      <span className="conversation-time">
                        {formatLastMessageTime(conversation.lastMessage?.timestamp)}
                      </span>
                    </div>
                    <p className="conversation-preview">
                      {truncateMessage(conversation.lastMessage?.message)}
                    </p>
                    <div className="conversation-meta">
                      <span className={`conversation-status ${onlineStatus[conversation.participant._id] ? 'online' : 'offline'}`}>
                        {getOnlineStatus(conversation.participant._id)}
                      </span>
                      {conversation.unreadCount > 0 && (
                        <span className="conversation-unread">
                          {conversation.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          {selectedConversation ? (
            <>
              <div className="chat-header">
                <div className="chat-header-left">
                  <button className="back-button" onClick={() => setSelectedConversation(null)}>
                    ←
                  </button>
                  <div className="chat-user-info">
                    <div className="chat-user-avatar">
                      {getInitials(selectedConversation.name)}
                      <div 
                        className="online-indicator"
                        style={{ backgroundColor: getStatusColor(selectedConversation._id) }}
                      ></div>
                    </div>
                    <div>
                      <h4 className="chat-user-name">{selectedConversation.name}</h4>
                      <p 
                        className="chat-user-status"
                        style={{ color: getStatusColor(selectedConversation._id) }}
                      >
                        {getLastSeenText(selectedConversation._id)}
                        {typing && <span className="typing-indicator"> • typing...</span>}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="chat-header-actions">
                  <button className="header-action-btn">📞</button>
                  <button className="header-action-btn">🎥</button>
                  <div className="chat-menu-container">
                    <button 
                      className="header-action-btn"
                      onClick={toggleChatMenu}
                    >
                      ⋯
                    </button>
                    {renderChatMenu()}
                  </div>
                </div>
              </div>

              <div 
                className="chat-messages" 
                ref={chatMessagesRef}
                onScroll={handleScroll}
              >
                {messageLoading ? (
                  <div className="chat-loading">
                    <div className="whatsapp-spinner"></div>
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="chat-empty">
                    <div className="chat-empty-icon">💬</div>
                    <p>No messages yet</p>
                    <p className="chat-empty-subtext">
                      Start a conversation with {selectedConversation.name}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="chat-date-label">
                      Today
                    </div>
                    {messages.map((message) => (
                      <div
                        key={message._id}
                        className={`chat-message ${
                          message.sender._id === employee._id
                            ? "chat-message-sent"
                            : "chat-message-received"
                        }`}
                      >
                        <div className="chat-message-content">
                          <p className="chat-message-text">{message.message}</p>
                          <span className="chat-message-time">
                            {formatTime(message.timestamp)}
                            {message.sender._id === employee._id && (
                              <span className={`message-status ${isMessageRead(message) ? 'read' : 'sent'}`}>
                                {isMessageRead(message) ? '✓✓' : '✓'}
                              </span>
                            )}
                          </span>
                          {message.sender._id === employee._id && (
                            <button 
                              className="delete-message-btn"
                              onClick={() => handleDeleteMessage(message)}
                              title="Delete message"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {typing && (
                      <div className="chat-message chat-message-received">
                        <div className="chat-message-content">
                          <div className="typing-bubble">
                            <div className="typing-dots">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
                
                {!isNearBottom && (
                  <button 
                    className="scroll-to-bottom-btn"
                    onClick={scrollToBottomButton}
                    title="Scroll to bottom"
                  >
                    ↓
                  </button>
                )}
              </div>

              <div className="chat-input-area">
                <div className="chat-actions">
                  <button 
                    className="emoji-picker-btn"
                    onClick={toggleEmojiPicker}
                    title="Add emoji"
                  >
                    😊
                  </button>
                  
                  <button 
                    className="file-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Send file"
                  >
                    📎
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    accept="*/*"
                  />
                </div>

                <form className="chat-input-form" onSubmit={handleSendMessage}>
                  <div className="chat-input-container">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Type a message..."
                      className="chat-input"
                      disabled={sending || messageLoading}
                    />
                    <button 
                      type="submit" 
                      className="chat-send-button"
                      disabled={sending || !newMessage.trim() || messageLoading}
                    >
                      {sending ? (
                        <div className="send-spinner"></div>
                      ) : (
                        <span className="send-icon">➤</span>
                      )}
                    </button>
                  </div>
                </form>

                {renderEmojiPicker()}
              </div>
            </>
          ) : (
            <div className="chat-placeholder">
              <div className="chat-placeholder-icon">💬</div>
              <h4>WhatsApp Web</h4>
              <p>Select a conversation to start messaging</p>
              <div className="placeholder-features">
                <div className="feature-item">
                  <span className="feature-icon">🔒</span>
                  <span>End-to-end encrypted</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">⚡</span>
                  <span>Real-time messaging</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {renderDeleteModal()}
      {renderDeleteChatModal()}
    </div>
  );
};

export default ChatsTab;